import { createContext, useContext, useState, useCallback, useRef, useEffect, ReactNode } from 'react'

export interface QueueItem {
  id: string
  articleId: string
  title: string
  source: string
  content: string
  estimatedDuration: number // seconds
}

interface AudioState {
  currentItem: QueueItem | null
  queue: QueueItem[]
  isPlaying: boolean
  isPaused: boolean
  currentTime: number
  duration: number
  playbackRate: number
  isLoading: boolean
}

interface AudioContextType extends AudioState {
  play: () => void
  pause: () => void
  resume: () => void
  toggle: () => void
  stop: () => void
  seek: (time: number) => void
  skipForward: (seconds?: number) => void
  skipBackward: (seconds?: number) => void
  setPlaybackRate: (rate: number) => void
  addToQueue: (article: QueueableArticle) => void
  removeFromQueue: (id: string) => void
  clearQueue: () => void
  moveInQueue: (fromIndex: number, toIndex: number) => void
  playNext: () => void
  playPrevious: () => void
  playArticle: (article: QueueableArticle) => void
}

// Type for articles that can be queued
export interface QueueableArticle {
  id: string
  title: string
  source?: string
  url?: string
  text_content?: string
  content?: string
}

const AudioContext = createContext<AudioContextType | null>(null)

export const PLAYBACK_RATES = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2, 2.5, 3]

const estimateDuration = (content: string, rate: number = 1): number => {
  const words = content.split(/\s+/).length
  return Math.ceil((words / 150) * 60 / rate) // 150 wpm base
}

export function AudioProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AudioState>({
    currentItem: null,
    queue: [],
    isPlaying: false,
    isPaused: false,
    currentTime: 0,
    duration: 0,
    playbackRate: 1,
    isLoading: false
  })

  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null)
  const synth = useRef(typeof window !== 'undefined' ? window.speechSynthesis : null)
  const progressInterval = useRef<ReturnType<typeof setInterval> | null>(null)
  const startTimeRef = useRef(0)

  // Clean up
  useEffect(() => {
    return () => {
      synth.current?.cancel()
      if (progressInterval.current) clearInterval(progressInterval.current)
    }
  }, [])

  // Track progress
  const startProgressTracking = useCallback((duration: number) => {
    startTimeRef.current = Date.now()

    if (progressInterval.current) clearInterval(progressInterval.current)

    progressInterval.current = setInterval(() => {
      const elapsed = (Date.now() - startTimeRef.current) / 1000
      setState(s => ({
        ...s,
        currentTime: Math.min(elapsed * s.playbackRate, duration)
      }))
    }, 500)
  }, [])

  const stopProgressTracking = useCallback(() => {
    if (progressInterval.current) {
      clearInterval(progressInterval.current)
      progressInterval.current = null
    }
  }, [])

  // Forward declare playNext for use in speak
  const playNextRef = useRef<() => void>(() => {})

  const speak = useCallback((item: QueueItem) => {
    if (!synth.current) return

    synth.current.cancel()
    stopProgressTracking()

    setState(s => ({ ...s, isLoading: true, currentItem: item }))

    const utterance = new SpeechSynthesisUtterance(item.content)
    utterance.rate = state.playbackRate

    // Get preferred voice
    const voices = synth.current.getVoices()
    const preferred = voices.find(v =>
      v.name.includes('Samantha') ||
      v.name.includes('Google US') ||
      (v.lang.startsWith('en') && v.localService)
    ) || voices.find(v => v.lang.startsWith('en'))

    if (preferred) utterance.voice = preferred

    const duration = estimateDuration(item.content, state.playbackRate)

    utterance.onstart = () => {
      setState(s => ({
        ...s,
        isPlaying: true,
        isPaused: false,
        isLoading: false,
        duration
      }))
      startProgressTracking(duration)
    }

    utterance.onend = () => {
      stopProgressTracking()
      setState(s => ({ ...s, isPlaying: false, isPaused: false }))
      playNextRef.current()
    }

    utterance.onerror = (e) => {
      if (e.error !== 'canceled') {
        console.error('TTS error:', e)
      }
      stopProgressTracking()
      setState(s => ({ ...s, isPlaying: false, isPaused: false, isLoading: false }))
    }

    utteranceRef.current = utterance
    synth.current.speak(utterance)
  }, [state.playbackRate, startProgressTracking, stopProgressTracking])

  const play = useCallback(() => {
    if (state.isPaused && synth.current?.paused) {
      synth.current.resume()
      setState(s => ({ ...s, isPlaying: true, isPaused: false }))
      startProgressTracking(state.duration)
    } else if (state.currentItem) {
      speak(state.currentItem)
    } else if (state.queue.length > 0) {
      const [next, ...rest] = state.queue
      setState(s => ({ ...s, queue: rest }))
      speak(next)
    }
  }, [state.isPaused, state.currentItem, state.queue, state.duration, speak, startProgressTracking])

  const pause = useCallback(() => {
    synth.current?.pause()
    stopProgressTracking()
    setState(s => ({ ...s, isPlaying: false, isPaused: true }))
  }, [stopProgressTracking])

  const resume = useCallback(() => {
    if (state.isPaused) {
      synth.current?.resume()
      setState(s => ({ ...s, isPlaying: true, isPaused: false }))
      startProgressTracking(state.duration)
    }
  }, [state.isPaused, state.duration, startProgressTracking])

  const toggle = useCallback(() => {
    if (state.isPlaying) {
      pause()
    } else {
      play()
    }
  }, [state.isPlaying, play, pause])

  const stop = useCallback(() => {
    synth.current?.cancel()
    stopProgressTracking()
    setState(s => ({
      ...s,
      isPlaying: false,
      isPaused: false,
      currentItem: null,
      currentTime: 0,
      duration: 0
    }))
  }, [stopProgressTracking])

  const seek = useCallback((time: number) => {
    // TTS doesn't support true seeking, restart at position
    if (!state.currentItem) return

    const content = state.currentItem.content
    const charPosition = Math.floor((time / state.duration) * content.length)
    const truncatedContent = content.slice(charPosition)

    synth.current?.cancel()

    const newItem = {
      ...state.currentItem,
      content: truncatedContent
    }

    setState(s => ({ ...s, currentTime: time }))
    speak(newItem)
  }, [state.currentItem, state.duration, speak])

  const skipForward = useCallback((seconds: number = 15) => {
    const newTime = Math.min(state.currentTime + seconds, state.duration)
    seek(newTime)
  }, [state.currentTime, state.duration, seek])

  const skipBackward = useCallback((seconds: number = 15) => {
    const newTime = Math.max(state.currentTime - seconds, 0)
    seek(newTime)
  }, [state.currentTime, seek])

  const setPlaybackRate = useCallback((rate: number) => {
    setState(s => ({ ...s, playbackRate: rate }))

    if (utteranceRef.current) {
      utteranceRef.current.rate = rate
    }

    // Restart with new rate if playing
    if (state.isPlaying && state.currentItem) {
      const currentProgress = state.currentTime / state.duration
      const content = state.currentItem.content
      const charPosition = Math.floor(currentProgress * content.length)

      synth.current?.cancel()

      const utterance = new SpeechSynthesisUtterance(content.slice(charPosition))
      utterance.rate = rate
      utteranceRef.current = utterance
      synth.current?.speak(utterance)
    }
  }, [state.isPlaying, state.currentItem, state.currentTime, state.duration])

  const addToQueue = useCallback((article: QueueableArticle) => {
    const item: QueueItem = {
      id: `${article.id}-${Date.now()}`,
      articleId: article.id,
      title: article.title,
      source: article.source || article.url || 'Unknown',
      content: article.text_content || article.content || '',
      estimatedDuration: estimateDuration(article.text_content || article.content || '', state.playbackRate)
    }

    setState(s => ({ ...s, queue: [...s.queue, item] }))
  }, [state.playbackRate])

  const removeFromQueue = useCallback((id: string) => {
    setState(s => ({ ...s, queue: s.queue.filter(item => item.id !== id) }))
  }, [])

  const clearQueue = useCallback(() => {
    setState(s => ({ ...s, queue: [] }))
  }, [])

  const moveInQueue = useCallback((fromIndex: number, toIndex: number) => {
    setState(s => {
      const newQueue = [...s.queue]
      const [item] = newQueue.splice(fromIndex, 1)
      newQueue.splice(toIndex, 0, item)
      return { ...s, queue: newQueue }
    })
  }, [])

  const playNext = useCallback(() => {
    stopProgressTracking()

    setState(s => {
      if (s.queue.length > 0) {
        const [next, ...rest] = s.queue
        // Schedule speak for next tick to avoid state issues
        setTimeout(() => speak(next), 0)
        return { ...s, queue: rest, currentTime: 0 }
      } else {
        return {
          ...s,
          currentItem: null,
          isPlaying: false,
          currentTime: 0,
          duration: 0
        }
      }
    })
  }, [speak, stopProgressTracking])

  // Update ref when playNext changes
  useEffect(() => {
    playNextRef.current = playNext
  }, [playNext])

  const playPrevious = useCallback(() => {
    // Restart current if past 5 seconds
    if (state.currentTime > 5 && state.currentItem) {
      seek(0)
    }
  }, [state.currentTime, state.currentItem, seek])

  const playArticle = useCallback((article: QueueableArticle) => {
    const item: QueueItem = {
      id: `${article.id}-${Date.now()}`,
      articleId: article.id,
      title: article.title,
      source: article.source || article.url || 'Unknown',
      content: article.text_content || article.content || '',
      estimatedDuration: estimateDuration(article.text_content || article.content || '', state.playbackRate)
    }

    speak(item)
  }, [state.playbackRate, speak])

  // Media Session API
  useEffect(() => {
    if (!('mediaSession' in navigator) || !state.currentItem) return

    navigator.mediaSession.metadata = new MediaMetadata({
      title: state.currentItem.title,
      artist: state.currentItem.source,
      album: 'Read Later'
    })

    navigator.mediaSession.setActionHandler('play', play)
    navigator.mediaSession.setActionHandler('pause', pause)
    navigator.mediaSession.setActionHandler('previoustrack', playPrevious)
    navigator.mediaSession.setActionHandler('nexttrack', playNext)
    navigator.mediaSession.setActionHandler('seekforward', () => skipForward(15))
    navigator.mediaSession.setActionHandler('seekbackward', () => skipBackward(15))

    return () => {
      navigator.mediaSession.setActionHandler('play', null)
      navigator.mediaSession.setActionHandler('pause', null)
    }
  }, [state.currentItem, play, pause, playPrevious, playNext, skipForward, skipBackward])

  // Persist queue
  useEffect(() => {
    localStorage.setItem('readl8r_audio_queue', JSON.stringify(state.queue))
  }, [state.queue])

  // Persist playback rate
  useEffect(() => {
    localStorage.setItem('readl8r_playback_rate', String(state.playbackRate))
  }, [state.playbackRate])

  // Load persisted data on mount
  useEffect(() => {
    const savedQueue = localStorage.getItem('readl8r_audio_queue')
    if (savedQueue) {
      try {
        const queue = JSON.parse(savedQueue)
        setState(s => ({ ...s, queue }))
      } catch (e) {
        // Ignore parse errors
      }
    }

    const savedRate = localStorage.getItem('readl8r_playback_rate')
    if (savedRate) {
      const rate = parseFloat(savedRate)
      if (PLAYBACK_RATES.includes(rate)) {
        setState(s => ({ ...s, playbackRate: rate }))
      }
    }
  }, [])

  const value: AudioContextType = {
    ...state,
    play,
    pause,
    resume,
    toggle,
    stop,
    seek,
    skipForward,
    skipBackward,
    setPlaybackRate,
    addToQueue,
    removeFromQueue,
    clearQueue,
    moveInQueue,
    playNext,
    playPrevious,
    playArticle
  }

  return (
    <AudioContext.Provider value={value}>
      {children}
    </AudioContext.Provider>
  )
}

export function useAudio() {
  const context = useContext(AudioContext)
  if (!context) {
    throw new Error('useAudio must be used within AudioProvider')
  }
  return context
}
